<template>
	<LiteDialogPanel class="recurrence-list-panel">
		<template #title>{{ translate( `title` ) }}</template>
		<template #body>
			<header class="body-header">
				<p class="description">{{ translate( `description` ) }}</p>
				<TabSet class="tabs" :tabs="tabs" @select="onTabClick" />
			</header>
			<main class="body-main">
				<template v-if="activeTab === `recurring`">
					<RecurrenceListPlaceholder
						v-if="!recurrences.length"
						:title="translate(`noRecurringMeetings`)"
						:button-text="translate(`addRecurringMeetingButton`)"
						class="placeholder"
						@create="$emit( `create`, activeTab )"
					/>
					<template v-else>
						<RecurrenceListItem
							v-for="r in recurrences"
							:key="r.id"
							class="recurrence"
							:name="r.name"
							:description="r.text"
							:managed-by="r.managedBy"
							:template-name="r.meetingTemplateName"
							@edit="$emit( `edit`, { activeTab, recurrence: r } )"
						/>
					</template>
				</template>
				<template v-if="activeTab === `oneTime`">
					<RecurrenceListPlaceholder
						v-if="!oneTimeMeetings.length"
						:title="translate(`noOneTimeMeetings`)"
						:button-text="translate(`addOneTimeMeetingButton`)"
						class="placeholder"
						@create="$emit( `create`, activeTab )"
					/>
					<template v-else>
						<RecurrenceListItem
							v-for="oneTime in oneTimeMeetings"
							:key="oneTime.id"
							class="recurrence"
							:name="oneTime.name"
							:description="oneTime.text"
							:managed-by="oneTime.managedBy"
							:template-name="oneTime.meetingTemplateName"
							@edit="$emit( `edit`, { activeTab, recurrence: oneTime } )"
						/>
					</template>
				</template>
			</main>
		</template>
		<template #footer>
			<TeamingButton2
				size="SMALL"
				@click="$emit( `create`, activeTab )"
			>
				{{ newMeetingButtonText }}
			</TeamingButton2>
		</template>
	</LiteDialogPanel>
</template>

<script>

import {
	LiteDialogPanel,
	TeamingButton2,
	TranslateMixin,
	TabSet
} from "@teaming/common-ui";

import RecurrenceListItem from "./RecurrenceListItem.vue";
import RecurrenceListPlaceholder from "./RecurrenceListPlaceholder.vue";

export const SCHEDULED_MEETING_TABS = {
	RECURRING: "recurring",
	ONE_TIME: "oneTime",
};

export default {
	name: "RecurrenceListPanel",
	teamingPackage: "common-team",
	mixins: [ TranslateMixin, ],
	emits: [ "create", "edit", ],
	components: {
		LiteDialogPanel,
		RecurrenceListItem,
		RecurrenceListPlaceholder,
		TeamingButton2,
		TabSet,
	},
	props: {
		recurrences: {
			type: Array,
			required: true,
			default: () => [],
		},
		oneTimeMeetings: {
			type: Array,
			required: true,
			default: () => [],
		},
	},
	data() {
		return {
			activeTab: SCHEDULED_MEETING_TABS.RECURRING,
		};
	},
	computed: {
		newMeetingButtonText() {
			return this.activeTab === SCHEDULED_MEETING_TABS.RECURRING ?
				this.translate( "newRecurringMeetingButton" ) :
				this.translate( "newOneTimeMeetingButton" );
		},
		tabs() {
			return [
				{
					id: SCHEDULED_MEETING_TABS.RECURRING,
					label: this.translate( "recurringTab" ),
					isActive: this.activeTab === SCHEDULED_MEETING_TABS.RECURRING,
				},
				{
					id: SCHEDULED_MEETING_TABS.ONE_TIME,
					label: this.translate( "oneTimeTab" ),
					isActive: this.activeTab === SCHEDULED_MEETING_TABS.ONE_TIME,
				},
			];
		},
	},
	methods: {
		onTabClick( tabId ) {
			this.activeTab = tabId;
		},
	},
};
</script>

<style lang="scss" scoped>
.recurrence-list-panel .body-header {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: stretch;
	gap: 0.5rem;

	.tabs {
		justify-content: space-evenly;
	}
}

.recurrence-list-panel .body-main {
	flex: 1;

	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	position: relative;
	@include auto-y-scroll();
}
</style>

<style lang="scss">
.recurrence-list-panel .sub-body {
	flex: 1;
	overflow-y: hidden;
}

.recurrence-list-panel .placeholder {
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	width: 100%;
}
</style>
